import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { HttpStatus } from 'config/Api.Config';
import { cloneDeep, isEmpty, merge } from 'lodash';
import contractSaveUnrestrictedApi from 'models/sales/contract/saveUnrestricted/Api.Contract.SaveUnrestricted';
import { useState } from 'react';
import { SalesContractAction, useSalesContractProvider } from '../../Sales.Contract.Hooks';
import ContractAdjustDistributionsDates from './tabs/Contract.AdjustDistributions.Dates';
import ContractAdjustDistributionsDistributions from './tabs/Contract.AdjustDistributions.Distributions';
import ContractAdjustDistributionsDocuments from './tabs/Contract.AdjustDistributions.Documents';
import ContractAdjustDistributionsEnhancementsDistributions from './tabs/Contract.AdjustDistributions.EnhancementsDistributions';
import ContractAdjustDistributionsSurcharges from './tabs/Contract.AdjustDistributions.Surcharges';

enum Tabs {
  distributions = 'distributions',
  enhancementsDistributions = 'enhancementsDistributions',
  documents = 'documents',
  dates = 'dates',
  surcharges = 'surcharges',
}
export type ContractAdjustForm = Partial<Model.IContract> & Api.Core.Contract.Save.IQuery & { isSaving: boolean };
export default function ContractAdjustDistributions() {
  const { lang } = getLocales();
  const { selected, findApi, listApi, pageRouter } = useSalesContractProvider();
  const [pendingSigningChange, setPendingSigningChange] = useState<ContractAdjustForm>();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  async function saveContract(data: ContractAdjustForm) {
    setIsSaving(true);
    const res = await contractSaveUnrestrictedApi(data, {
      sendDocumentForSigning: data.sendDocumentForSigning,
      generateDocuments: data.generateDocuments,
      documentSigningType: data.documentSigningType,
    });
    if (res.status !== HttpStatus.ok) {
      addLog({ error: res.message });
      setIsSaving(false);
    } else {
      addLog({ success: lang.saveSuccess });
      setIsSaving(false);
      setPendingSigningChange(undefined);
      pageRouter.updateParams({ id: selected.id.toString(), action: SalesContractAction.INFO });
      listApi.execute((body) => body);
      findApi.execute((body) => body);
    }
  }

  return (
    <>
      <ModalForm<ContractAdjustForm>
        subcomponents={ThemeModalFormDefault}
        dependencyArray={[selected]}
        title={lang.editContractUnrestricted}
        default={{
          ...selected?.toJSON(),
          sendDocumentForSigning: false,
          generateDocuments: false,
          documentSigningType: selected?.documentSigningType ?? selected?.product?.documentSigningType,
          contractEnhancements: selected?.contractEnhancements ?? [],
          subfees: selected?.subfees ?? [],
        }}
        onCancel={() => pageRouter.updateParams({ id: selected.id.toString(), action: SalesContractAction.INFO })}
        onSubmit={async (data) => {
          if (data.documentSigningType !== selected?.documentSigningType) {
            setPendingSigningChange(data);
            return;
          }
          await saveContract(data);
        }}
        tabs={[
          { id: Tabs.distributions, icon: 'fas-coins', text: lang.distributions },
          {
            id: Tabs.enhancementsDistributions,
            icon: 'fas-coins',
            text: lang.enhancementsDistributions,
            disabled: isEmpty(selected?.contractEnhancements),
          },
          {
            id: Tabs.surcharges,
            icon: 'fas-plus',
            text: lang.surcharges,
            disabled: isEmpty(selected?.subfees),
          },
          { id: Tabs.documents, icon: 'fas-folder', text: lang.documents },
          { id: Tabs.dates, icon: 'fas-calendar-alt', text: lang.dates },
        ]}
        render={({ activeTab }) => {
          return activeTab?.id === Tabs.distributions ? (
            <ContractAdjustDistributionsDistributions />
          ) : activeTab?.id === Tabs.enhancementsDistributions ? (
            <ContractAdjustDistributionsEnhancementsDistributions />
          ) : activeTab?.id === Tabs.surcharges ? (
            <ContractAdjustDistributionsSurcharges />
          ) : activeTab?.id === Tabs.documents ? (
            <ContractAdjustDistributionsDocuments />
          ) : activeTab?.id === Tabs.dates ? (
            <ContractAdjustDistributionsDates />
          ) : null;
        }}
      />
      {pendingSigningChange && (
        <Confirm<ContractAdjustForm>
          onClose={() => {
            setPendingSigningChange(undefined);
            setIsSaving(false);
          }}
          subcomponents={merge(cloneDeep(ThemeConfirmDefault), {
            modal: {
              page: {
                style: { zIndex: '1001 !important', backgroundColor: `${Theme.Color.shadowDarker} !important` },
              },
            },
          })}
          actionName={lang.changeDocumentSigningType}
          info={lang.changeDocumentSigningTypeWarning}
          confirmationType="nextButton"
          onConfirm={async () => {
            await saveContract(pendingSigningChange);
            setPendingSigningChange(undefined);
          }}
          isLoading={isSaving}
        />
      )}
    </>
  );
}
